/**
 * Module-level access-token store. IN MEMORY ONLY — never persisted to
 * localStorage, sessionStorage, or any cookie.
 *
 * The decoded JWT payload is cached alongside the token so consumers don't
 * decode on every read.
 */
import { decodeJwtPayload, type JwtPayload } from '@/utils/jwt';

interface StoreState {
  token: string | null;
  expiresAt: string | null;
  claims: JwtPayload | null;
  csrfToken: string | null;
}

const state: StoreState = {
  token: null,
  expiresAt: null,
  claims: null,
  csrfToken: null,
};

let signOutCallback: (() => void) | null = null;

export function getAccessToken(): string | null {
  return state.token;
}

export function getClaims(): JwtPayload | null {
  return state.claims;
}

export function getExpiresAt(): string | null {
  return state.expiresAt;
}

/**
 * `expiresAt` is widened to `string | number | null` on purpose. saas-auth
 * sends an ISO-8601 instant; the canonical factory's `ApiClientConfig` types
 * the parameter as `number | null`. A function accepting the WIDER type is
 * assignable where the narrower one is expected, so this stays compatible with
 * the vendored factory without forking it, and normalizes either form to ISO.
 */
export function setAccessToken(
  token: string | null,
  expiresAt: string | number | null = null,
): void {
  state.token = token;
  state.expiresAt = normalizeExpiry(expiresAt);
  state.claims = token ? decodeJwtPayload(token) : null;
}

function normalizeExpiry(value: string | number | null): string | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string') return value;
  const d = new Date(value);
  return Number.isFinite(d.getTime()) ? d.toISOString() : null;
}

export function getCsrfToken(): string | null {
  return state.csrfToken;
}

/** Optional in-memory CSRF token captured from the refresh response body.
 *  NEVER persisted. Cleared by `signOut()`. */
export function setCsrfToken(token: string | null): void {
  state.csrfToken = token;
}

export function registerSignOutCallback(cb: (() => void) | null): void {
  signOutCallback = cb;
}

export function signOut(): void {
  state.token = null;
  state.expiresAt = null;
  state.claims = null;
  state.csrfToken = null;
  signOutCallback?.();
}

/** Test seam. */
export function __resetAuthStore(): void {
  state.token = null;
  state.expiresAt = null;
  state.claims = null;
  state.csrfToken = null;
  signOutCallback = null;
}
